import { Code, FolderOpen } from 'lucide-react'
import { useAudiences } from '@/api/hooks/use-audiences'
import { useDomains } from '@/api/hooks/use-domains'

type ContentType = 'articles' | 'html'

interface Props {
  name: string
  onNameChange: (v: string) => void
  audienceId: string
  onAudienceChange: (v: string) => void
  fromAddress: string
  onFromAddressChange: (v: string) => void
  replyTo: string
  onReplyToChange: (v: string) => void
  subject: string
  onSubjectChange: (v: string) => void
  contentType: ContentType
  onContentTypeChange: (v: ContentType) => void
  onNext: () => void
}

export function CampaignStepSetup({
  name,
  onNameChange,
  audienceId,
  onAudienceChange,
  fromAddress,
  onFromAddressChange,
  replyTo,
  onReplyToChange,
  subject,
  onSubjectChange,
  contentType,
  onContentTypeChange,
  onNext,
}: Props): React.JSX.Element {
  const { data: audienceData } = useAudiences()
  const { data: domainData } = useDomains()
  const audiences = audienceData?.data ?? []
  const allDomains = domainData?.data ?? []

  const canProceed = name.trim() && audienceId && subject.trim()
  const nextLabel = contentType === 'articles' ? 'Next: Pick Articles →' : 'Next: Write Content →'

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <label htmlFor="c-name" className="block text-sm font-medium text-text-primary">
          Campaign name
        </label>
        <input
          id="c-name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Newsletter 27/3-2026"
          className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
        />
      </div>

      <div>
        <label htmlFor="c-audience" className="block text-sm font-medium text-text-primary">
          Audience
        </label>
        <select
          id="c-audience"
          value={audienceId}
          onChange={(e) => onAudienceChange(e.target.value)}
          className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2.5 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
        >
          <option value="">Select an audience</option>
          {audiences.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.subscriber_count.toLocaleString()})
            </option>
          ))}
        </select>
      </div>

      <FromAddressInput
        value={fromAddress}
        onChange={onFromAddressChange}
        domains={allDomains}
      />

      <div>
        <label htmlFor="c-subject" className="block text-sm font-medium text-text-primary">
          Subject line
        </label>
        <input
          id="c-subject"
          type="text"
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          placeholder="This week: Business Class deals + Helsinki city break"
          className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
        />
      </div>

      <div>
        <label htmlFor="c-reply" className="block text-sm font-medium text-text-primary">
          Reply-to <span className="text-text-tertiary">(optional)</span>
        </label>
        <input
          id="c-reply"
          type="email"
          value={replyTo}
          onChange={(e) => onReplyToChange(e.target.value)}
          placeholder="editor@maxyourpoints.com"
          className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none"
        />
      </div>

      {/* Content type toggle */}
      <div>
        <p className="mb-2 text-sm font-medium text-text-primary">Content type</p>
        <div className="grid grid-cols-2 gap-3">
          <ContentTypeCard
            value="articles"
            selected={contentType}
            onSelect={onContentTypeChange}
            icon={FolderOpen}
            title="WordPress Articles"
            desc="Pick a curated article group from your WordPress site"
          />
          <ContentTypeCard
            value="html"
            selected={contentType}
            onSelect={onContentTypeChange}
            icon={Code}
            title="Custom HTML"
            desc="Write or paste your own email HTML"
          />
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-40"
        >
          {nextLabel}
        </button>
      </div>
    </div>
  )
}

function ContentTypeCard({
  value,
  selected,
  onSelect,
  icon: Icon,
  title,
  desc,
}: {
  value: ContentType
  selected: ContentType
  onSelect: (v: ContentType) => void
  icon: typeof FolderOpen
  title: string
  desc: string
}): React.JSX.Element {
  const active = value === selected
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`rounded-xl border p-4 text-left transition-all ${active ? 'border-brand-500 bg-brand-50 shadow-sm' : 'border-surface-3 hover:bg-surface-1'}`}
    >
      <Icon className={`mb-2 h-5 w-5 ${active ? 'text-brand-600' : 'text-text-tertiary'}`} />
      <p className={`text-sm font-medium ${active ? 'text-brand-700' : 'text-text-primary'}`}>
        {title}
      </p>
      <p className="mt-0.5 text-xs text-text-secondary">{desc}</p>
    </button>
  )
}

function FromAddressInput({
  value,
  onChange,
  domains,
}: {
  value: string
  onChange: (v: string) => void
  domains: Array<{ id: string; domain: string; status: string }>
}): React.JSX.Element {
  const atIdx = value.indexOf('@')
  const localPart = atIdx >= 0 ? value.slice(0, atIdx) : value || 'newsletter'
  const domainPart = atIdx >= 0 ? value.slice(atIdx + 1) : ''

  function handleLocalChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const local = e.target.value.replace(/[^a-zA-Z0-9._+-]/g, '')
    onChange(local && domainPart ? `${local}@${domainPart}` : local)
  }

  function handleDomainChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const domain = e.target.value
    const local = localPart || 'newsletter'
    onChange(domain ? `${local}@${domain}` : '')
  }

  if (domains.length === 0) {
    return (
      <div>
        <label className="block text-sm font-medium text-text-primary">From address</label>
        <p className="mt-1 text-sm text-text-secondary">
          No domains yet.{' '}
          <a href="/domains" className="font-medium text-brand-500 hover:text-brand-600">
            + Add domain
          </a>
        </p>
      </div>
    )
  }

  return (
    <div>
      <label className="block text-sm font-medium text-text-primary">From address</label>
      <div className="mt-1 flex items-center rounded-lg border border-surface-3 bg-surface-0 transition-colors focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
        <input
          type="text"
          value={localPart}
          onChange={handleLocalChange}
          placeholder="newsletter"
          className="min-w-0 flex-1 border-none bg-transparent py-2.5 pl-3 pr-0 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-0"
        />
        <span className="shrink-0 px-0.5 text-sm text-text-tertiary">@</span>
        <select
          value={domainPart}
          onChange={handleDomainChange}
          className="min-w-0 flex-[2] appearance-none border-none bg-transparent py-2.5 pl-0 pr-8 text-sm font-medium text-text-primary focus:outline-none focus:ring-0"
        >
          <option value="">Select domain</option>
          {domains.map((d) => (
            <option key={d.id} value={d.domain}>
              {d.domain}{d.status !== 'active' ? ` (${d.status})` : ''}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
